import { RefreshCw } from 'lucide-react';
import type { LBNodeData } from '@/proto/logiblocks/logiblocks';
import { CONTROL_FLOW_ICON_COLOR } from '../../styles';
import {
  type GraphData,
  type LBNodeInfo,
  NodeCategory,
  OutputType,
} from '../../types';
import { buildNodeData } from '../../nodeDataUtils';
import { MSG_REQUIRED } from '../../validationMessages';
import { BackToDelayCustomization } from './BackToDelayCustomization';
import { delayLabel, getDelaysAbove } from './delaysAbove';

/** Validation field names for the Back to delay step. Shared with the UI. */
export const BackToDelayFields = {
  RETURN_TO: 'Return to',
} as const;

export const MSG_RETURN_TO_MISSING =
  'The Delay this step returns to is no longer above it. Choose a Delay again.';

export const MSG_ONLY_AT_THE_END =
  'Back to delay ends a branch, so it can only go at the end of one.';

export const MSG_NO_DELAY_ABOVE = 'Add this step below a Delay until event.';

export const BackToDelayNodeInfo: LBNodeInfo = {
  protobufNodeDataKey: 'back_to_delay',
  category: NodeCategory.CONTROL_FLOW,
  label: 'Back to delay',
  hasInput: true,
  getIcon: (size: number) => (
    <RefreshCw size={size} color={CONTROL_FLOW_ICON_COLOR} />
  ),
  outputType: OutputType.SINGLE,
  // The run goes back to the Delay, so nothing after this step could ever run.
  endsBranch: true,

  getSubtitle: (data: LBNodeData, graph: GraphData) => {
    const target = graph.nodes.find(
      (n) => n.id === data.back_to_delay?.return_to_node_id
    );
    return target ? delayLabel(target, graph.nodes) : undefined;
  },

  getPickerUnavailableReason: (parentNodeId: string, graph: GraphData) => {
    if (
      getDelaysAbove(parentNodeId, graph.nodes, graph.edges, {
        includingNode: true,
      }).length === 0
    ) {
      return MSG_NO_DELAY_ABOVE;
    }
    // Adding a step under one that has a child puts the child below the new step.
    return graph.edges.some((e) => e.source === parentNodeId)
      ? MSG_ONLY_AT_THE_END
      : undefined;
  },

  getValidationErrors: (
    nodeId: string,
    nodeData: LBNodeData,
    graph: GraphData
  ) => {
    const returnTo = nodeData.back_to_delay?.return_to_node_id;
    if (!returnTo) {
      return [
        {
          type: 'REQUIRED_FIELD',
          field: BackToDelayFields.RETURN_TO,
          message: MSG_REQUIRED,
        },
      ];
    }
    const stillAbove = getDelaysAbove(nodeId, graph.nodes, graph.edges).some(
      (delay) => delay.id === returnTo
    );
    return stillAbove
      ? undefined
      : [
          {
            type: 'VALUE_ERROR',
            field: BackToDelayFields.RETURN_TO,
            message: MSG_RETURN_TO_MISSING,
          },
        ];
  },

  CustomizationComponent: BackToDelayCustomization,
  showCustomizePanelOnCreation: true,
  createDefaultNodeData: () =>
    buildNodeData('back_to_delay', { return_to_node_id: '' }),
};
